import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Home, Menu, MessageSquare, Package, ReceiptText, Tag, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import PageHero from "../components/PageHero";
import heroImage from "../assets/zeamays.png";
import { isAdminUser } from "../services/admin";
import { deleteAdminReview, loadAdminReviews, updateAdminReview } from "../services/reviews";

const navItems = [
  { label: "Dashboard", href: "/admin", icon: Home },
  { label: "Orders", href: "/admin/orders", icon: ReceiptText },
  { label: "Blog", href: "/admin/blog", icon: FileText },
  { label: "Products", href: "/admin/products", icon: Package },
  { label: "Reviews", href: "/admin/reviews", icon: MessageSquare },
  { label: "Promotions", href: "/admin/promotions", icon: Tag },
];

const AdminReviews = () => {
  const { user, loading } = useAuth();
  const [authorized, setAuthorized] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [error, setError] = useState("");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const refresh = async () => {
    try {
      setError("");
      setReviews(await loadAdminReviews());
    } catch (err) {
      setError(err.message || "Unable to load reviews.");
    }
  };

  useEffect(() => {
    let cancelled = false;
    const checkAccess = async () => {
      if (!user) {
        setCheckingAccess(false);
        return;
      }
      try {
        const allowed = await isAdminUser(user);
        if (cancelled) return;
        setAuthorized(allowed);
        if (allowed) await refresh();
      } catch (err) {
        if (!cancelled) setError(err.message || "Unable to verify admin access.");
      } finally {
        if (!cancelled) setCheckingAccess(false);
      }
    };
    checkAccess();
    return () => { cancelled = true; };
  }, [user]);

  const moderate = async (id, updates) => {
    try {
      await updateAdminReview(id, updates);
      await refresh();
    } catch (err) {
      setError(err.message || "Unable to update review.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await deleteAdminReview(id);
      await refresh();
    } catch (err) {
      setError(err.message || "Unable to delete review.");
    }
  };

  if (loading || checkingAccess) return <AdminLoading />;
  if (!authorized) {
    return (
      <div className="min-h-screen bg-lime-50 px-6 py-28 text-center text-slate-700">
        <div className="mx-auto max-w-xl rounded-lg border border-red-200 bg-white p-8 shadow-sm">
          <h1 className="text-3xl font-semibold text-red-700">Access denied</h1>
          <p className="mt-4 text-lg">This page is only available to Zea Mays admin accounts.</p>
          <Link to="/admin" className="mt-6 inline-block rounded-md bg-red-700 px-5 py-3 text-white">Back to dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7fbef] pt-16 text-slate-900">
      <div className="lg:flex">
        <aside className="fixed bottom-0 left-0 top-16 hidden w-64 border-r border-lime-200 bg-white lg:block"><AdminSidebar /></aside>
        <header className="sticky top-16 z-30 border-b border-lime-200 bg-white/95 px-4 py-3 backdrop-blur lg:hidden">
          <div className="flex items-center justify-between">
            <div><p className="text-xs font-bold uppercase tracking-[0.22em] text-red-700">Zea Mays</p><h1 className="text-lg font-bold text-slate-950">Review Management</h1></div>
            <button type="button" onClick={() => setMobileNavOpen((value) => !value)} className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-lime-200 text-red-700" aria-label="Toggle admin navigation">
              {mobileNavOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
          {mobileNavOpen ? <nav className="mt-3 grid gap-2">{navItems.map((item) => <AdminNavLink key={item.label} item={item} onClick={() => setMobileNavOpen(false)} />)}</nav> : null}
        </header>

        <main className="w-full px-4 py-6 sm:px-6 lg:ml-64 lg:px-8 lg:py-8">
          <div className="mx-auto max-w-7xl">
            <div className="mb-8">
              <PageHero
                eyebrow="Review workspace"
                title="Moderate customer feedback with confidence."
                description="Approve submitted customer experiences, feature the strongest feedback, and keep the public Reviews page trustworthy."
                image={heroImage}
                imageAlt="Zea Mays review management"
                primaryAction={{ to: "/admin", label: "Back to Dashboard" }}
                secondaryAction={{ to: "/reviews", label: "View Public Reviews" }}
                stats={[
                  { label: "Submitted", value: `${reviews.length} reviews` },
                  { label: "Pending", value: `${reviews.filter((review) => !review.is_approved).length}` },
                  { label: "Published", value: `${reviews.filter((review) => review.is_approved).length}` },
                  { label: "Featured", value: `${reviews.filter((review) => review.is_featured).length}` },
                ]}
                compact
              />
            </div>
            {error ? <p className="mb-5 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</p> : null}
            <div className="overflow-x-auto rounded-lg border border-lime-200 bg-white shadow-sm">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-lime-50"><tr><th className="p-4">Customer / review</th><th className="p-4">Rating</th><th className="p-4">Status</th><th className="p-4">Actions</th></tr></thead>
                <tbody className="divide-y divide-lime-100">
                  {reviews.map((review) => (
                    <tr key={review.id} className="align-top">
                      <td className="max-w-xl p-4"><strong>{review.customer_name}</strong><p className="mt-1 text-slate-600">{review.review_text}</p><p className="mt-2 text-xs text-slate-500">Order: {review.order_id}{review.verified_purchase ? " - Verified purchase" : ""}</p></td>
                      <td className="p-4">{review.rating}/5</td>
                      <td className="p-4">{review.is_approved ? "Published" : "Pending"}{review.is_featured ? " - Featured" : ""}</td>
                      <td className="space-x-2 p-4">
                        <button type="button" onClick={() => moderate(review.id, { is_approved: !review.is_approved })} className="rounded-lg bg-lime-100 px-3 py-2 font-semibold">{review.is_approved ? "Hide" : "Approve"}</button>
                        <button type="button" onClick={() => moderate(review.id, { is_featured: !review.is_featured })} className="rounded-lg bg-amber-100 px-3 py-2 font-semibold">{review.is_featured ? "Unfeature" : "Feature"}</button>
                        <button type="button" onClick={() => remove(review.id)} className="rounded-lg bg-red-100 px-3 py-2 font-semibold text-red-700">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!reviews.length ? <p className="p-10 text-center text-slate-500">No submitted reviews.</p> : null}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

const AdminLoading = () => <div className="min-h-screen bg-lime-50 px-6 py-28 text-center text-slate-700"><p className="text-lg font-semibold">Checking admin access...</p></div>;

const AdminSidebar = () => <div className="flex h-full flex-col px-5 py-6"><div className="border-b border-lime-200 pb-6"><p className="text-xs font-bold uppercase tracking-[0.28em] text-red-700">Zea Mays</p><h2 className="mt-2 text-2xl font-bold text-slate-950">Admin</h2></div><nav className="mt-6 grid gap-2">{navItems.map((item) => <AdminNavLink key={item.label} item={item} />)}</nav></div>;

const AdminNavLink = ({ item, onClick }) => { const Icon = item.icon; return <Link to={item.href} onClick={onClick} className="flex items-center gap-3 rounded-md px-3 py-3 text-sm font-semibold text-slate-700 transition hover:bg-lime-100 hover:text-red-700"><Icon size={18} />{item.label}</Link>; };

export default AdminReviews;
